import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ShoppingBag, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../contexts/CartContext";
import { useI18n } from "../contexts/I18nContext";
import { downloadProductionSheet } from "../lib/downloadProductionSheet";
import { describeConfiguration } from "../lib/orderSummary";
import VolumeDiscountRow from "../components/VolumeDiscountRow";

export default function Cart() {
  const { items, removeItem, updateQty, totalItems } = useCart();
  const { t, lang } = useI18n();
  const [downloading, setDownloading] = useState(false);

  const handleDownloadSheet = async () => {
    if (downloading || items.length === 0) return;
    setDownloading(true);
    try {
      await downloadProductionSheet({ items, lang });
      toast.success(
        lang === "fr"
          ? "Fiche de production téléchargée"
          : "Production sheet downloaded",
      );
    } catch (e) {
      toast.error(
        lang === "fr"
          ? "Erreur lors de la génération du PDF"
          : "PDF generation error",
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="cart-page" className="min-h-screen">
      <section className="relative py-20 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="label-tech">{t.cart.title}</span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-bold uppercase tracking-tight">
            <span className="chrome-text">{t.cart.title}</span>
          </h1>
          <p className="mt-3 font-mono text-xs uppercase tracking-widest text-zinc-500">
            {totalItems} {t.cart.total_items}
          </p>
        </div>
      </section>

      {items.length === 0 ? (
        <section className="max-w-3xl mx-auto px-6 py-24 text-center">
          <ShoppingBag size={56} className="text-zinc-700 mx-auto mb-6" />
          <p className="text-zinc-400 font-body text-lg mb-8">{t.cart.empty}</p>
          <Link to="/produits" className="btn-neon inline-flex" data-testid="cart-empty-shop">
            {t.cart.continue}
          </Link>
        </section>
      ) : (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[1fr_380px] gap-10">
          {/* Items */}
          <ul className="space-y-4" data-testid="cart-items-list">
            {items.map((item) => (
              <li
                key={item.lineId}
                className="flex gap-5 bg-[#080808] border border-white/5 p-5"
                data-testid={`cart-page-item-${item.lineId}`}
              >
                {(() => {
                  const previewImg = item.configuration?.previewImage;
                  return previewImg ? (
                    <img
                      src={previewImg}
                      alt="Aperçu configuré"
                      className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 object-contain bg-black border border-[#39FF14]/30"
                      data-testid={`cart-item-preview-${item.lineId}`}
                    />
                  ) : (
                    <img
                      src={item.image || "/placeholder-product.svg"}
                      alt=""
                      className={`w-28 h-28 sm:w-32 sm:h-32 shrink-0 ${item.image ? "object-cover" : "object-contain bg-[#0f0f0f] p-4 border border-white/5"}`}
                      onError={(e) => { e.currentTarget.src = "/placeholder-product.svg"; }}
                    />
                  );
                })()}
                <div className="flex-1 min-w-0 flex flex-col">
                  <h3 className="font-display text-lg font-semibold text-white leading-tight">
                    {item.product_name?.[lang] || item.product_name?.fr || item.product_name || item.product_slug}
                  </h3>
                  {typeof item.unit_price_chf === "number" && item.unit_price_chf > 0 && (
                    <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-[#39FF14]" data-testid={`cart-item-unit-price-${item.lineId}`}>
                      {Math.round(item.unit_price_chf).toLocaleString("fr-CH")} CHF HT
                      {item.quantity > 1 && ` × ${item.quantity} = ${Math.round(item.unit_price_chf * item.quantity).toLocaleString("fr-CH")} CHF HT`}
                    </div>
                  )}
                  {item.configuration && Object.keys(item.configuration).length > 0 && (() => {
                    const descLines = describeConfiguration(item.product_slug, item.configuration, lang);
                    return descLines.length > 0 ? (
                      <ul className="mt-2 space-y-0.5" data-testid={`cart-item-desc-${item.lineId}`}>
                        {descLines.map((l, i) => (
                          <li key={i} className="text-xs text-zinc-400 font-mono truncate">
                            <span className="text-[#39FF14] mr-1">·</span>{l}
                          </li>
                        ))}
                      </ul>
                    ) : null;
                  })()}
                  <div className="mt-auto flex items-center gap-4">
                    <div className="flex items-center border border-white/10">
                      <button
                        onClick={() => updateQty(item.lineId, item.quantity - 1)}
                        className="w-9 h-9 text-zinc-400 hover:text-[#39FF14]"
                        data-testid={`cart-page-decrement-${item.lineId}`}
                      >
                        −
                      </button>
                      <span className="w-10 text-center font-mono text-sm">{item.quantity}</span>
                      <button
                        onClick={() => updateQty(item.lineId, item.quantity + 1)}
                        className="w-9 h-9 text-zinc-400 hover:text-[#39FF14]"
                        data-testid={`cart-page-increment-${item.lineId}`}
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => removeItem(item.lineId)}
                      className="ml-auto inline-flex items-center gap-2 text-zinc-500 hover:text-red-400 transition-colors text-xs uppercase tracking-widest font-mono"
                      data-testid={`cart-page-remove-${item.lineId}`}
                    >
                      <Trash2 size={14} />
                      {t.cart.remove}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* Summary */}
          <aside className="bg-[#080808] border border-white/5 p-6 h-fit sticky top-28">
            <div className="label-tech mb-4">{t.cart.summary}</div>
            <div className="flex items-center justify-between py-3 border-b border-white/10">
              <span className="text-zinc-400 font-body text-sm">{t.cart.total_items}</span>
              <span className="font-display text-2xl font-bold text-[#39FF14]">{totalItems}</span>
            </div>
            {(() => {
              const subtotal = items.reduce((s, it) => s + (Number(it.unit_price_chf) || 0) * (it.quantity || 1), 0);
              if (subtotal <= 0) return null;
              return <VolumeDiscountRow subtotal={subtotal} lang={lang} />;
            })()}
            <p className="text-xs text-zinc-500 font-body mt-4 mb-6 leading-relaxed">
              {t.cart.note}
            </p>
            <Link
              to="/checkout"
              className="btn-neon w-full justify-center"
              data-testid="cart-page-checkout"
            >
              {t.cart.checkout}
            </Link>
            <button
              type="button"
              onClick={handleDownloadSheet}
              disabled={downloading || items.length === 0}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-[#39FF14]/40 bg-transparent text-[#39FF14] font-mono text-[11px] uppercase tracking-widest py-3 hover:bg-[#39FF14]/10 hover:border-[#39FF14] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cart-download-sheet-button"
              title={lang === "fr"
                ? "Télécharger la fiche technique de production (PDF)"
                : "Download production technical sheet (PDF)"}
            >
              {downloading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <FileDown size={14} />
              )}
              {downloading
                ? (lang === "fr" ? "Génération…" : "Generating…")
                : (lang === "fr"
                    ? "Télécharger fiche production"
                    : lang === "de"
                    ? "Produktionsblatt herunterladen"
                    : lang === "it"
                    ? "Scarica scheda produzione"
                    : "Download production sheet")}
            </button>
            <Link
              to="/produits"
              className="block text-center mt-4 font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-[#39FF14]"
              data-testid="cart-page-continue"
            >
              {t.cart.continue}
            </Link>
          </aside>
        </section>
      )}
    </div>
  );
}
